import { z } from 'zod';

/**
 * Phase 5 chunk 2 — Workflow editor DTOs.
 *
 * Mirror of WorkflowService input shapes + projection schemas.  Both
 * sides stay in lockstep — adding a field requires touching this
 * file AND the service AND the DB column (migration 0075 / 0076).
 *
 * Chunk 3a: `kind` (3-bucket) was split into `category` (5-bucket
 * display / reporting taxonomy) + `isInitial` (boolean — is this where
 * new tasks spawn).  The two axes are orthogonal — see
 * `_workflow-validators.ts` for the post-change invariants.
 *
 * See docs/ai-platform/09-ROADMAP.md § 7.
 */

export const WorkflowStateCategory = z.enum([
  'open',
  'in_progress',
  'review',
  'done',
  'cancelled',
]);
export type WorkflowStateCategoryValue = z.infer<typeof WorkflowStateCategory>;

const StateKeyRegex = /^[a-z][a-z0-9_-]*$/;
const RolePresets = ['OWNER', 'MANAGER', 'LEAD', 'MEMBER', 'OUTSTAFF', 'VIEWER'] as const;
const UuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Phase 5 chunk 6 — role gate on a transition.
 *
 * Accepts either a preset name (`OWNER`..`VIEWER`) for hierarchy
 * checks OR a custom-role UUID (a row in `core.team_role_definitions`
 * scoped to the workflow's team).
 *
 * Runtime semantics (see `WorkflowTaskQueriesService.checkTransition`):
 *   - preset gate → actor's effective `presetForPriority` must rank ≥
 *     the gate (hierarchical).
 *   - custom-role gate → actor must either (a) carry that exact
 *     `customRoleId` on their team membership, OR (b) be OWNER on the
 *     team (universal override).
 *
 * Stored unchanged in the existing `required_role_preset` text column.
 * Migration 0083 widens the DB CHECK to accept either form.
 */
export const WorkflowRoleRefSchema = z.string().refine(
  (val) =>
    (RolePresets as readonly string[]).includes(val) || UuidRegex.test(val),
  {
    message:
      'must be a preset name (OWNER|MANAGER|LEAD|MEMBER|OUTSTAFF|VIEWER) or a custom-role UUID',
  },
);
export type WorkflowRoleRef = z.infer<typeof WorkflowRoleRefSchema>;

/**
 * One state in the workflow.  `key` is the stable machine identifier
 * (referenced by transitions + tasks once chunk 3 wires task.status).
 * `ordinal` controls display order in the editor + task selector.
 * `isInitial` flags the single state new tasks land in — exactly one
 * row per workflow must set it true (validator enforces).
 */
export const WorkflowStateInputSchema = z.object({
  key: z.string().regex(StateKeyRegex, {
    message:
      "key must be lowercase letter then [a-z0-9_]*, e.g. 'todo' or 'in_review'",
  }).max(80),
  label: z.string().min(1).max(120),
  ordinal: z.number().int().min(0),
  category: WorkflowStateCategory,
  isInitial: z.boolean().default(false),
  color: z.string().min(1).max(40).optional(),
});
export type WorkflowStateInput = z.infer<typeof WorkflowStateInputSchema>;

/**
 * One allowed edge in the workflow graph.  References states by `key`
 * (not by id — at create time the caller doesn't know the ids yet;
 * service resolves keys → ids inside the create transaction).
 */
export const WorkflowTransitionInputSchema = z.object({
  fromStateKey: z.string().regex(StateKeyRegex).max(80),
  toStateKey: z.string().regex(StateKeyRegex).max(80),
  label: z.string().min(1).max(80),
  /** Preset name or custom-role UUID — see `WorkflowRoleRefSchema`. */
  requiredRolePreset: WorkflowRoleRefSchema.optional(),
  requiresApproval: z.boolean().default(false),
});
export type WorkflowTransitionInput = z.infer<typeof WorkflowTransitionInputSchema>;

export const CreateWorkflowSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  /** Optional per-project scope.  Null/omitted = team-wide. */
  projectId: z.string().uuid().optional(),
  defaultForTeam: z.boolean().default(false),
  /** Atomic create: every state + every transition must validate
   *  against the projected graph before any row lands. */
  states: z.array(WorkflowStateInputSchema).min(2).max(40),
  transitions: z.array(WorkflowTransitionInputSchema).min(1).max(200),
});
export type CreateWorkflowInput = z.infer<typeof CreateWorkflowSchema>;

export const UpdateWorkflowMetaSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  enabled: z.boolean().optional(),
  /** Set true to flip this workflow into the team's default slot;
   *  service un-sets any other current default in the same transaction. */
  defaultForTeam: z.boolean().optional(),
});
export type UpdateWorkflowMetaInput = z.infer<typeof UpdateWorkflowMetaSchema>;

/**
 * Add one state, optionally WITH its edges (W5-D status-list editor): a new
 * non-initial state needs an incoming edge (and a non-terminal one an
 * outgoing edge), so without them every add is `workflow_state_orphan`.
 * Every edge must touch the new state; the server validates the projected
 * graph and inserts the state and its edges in one transaction.
 */
export const AddStateSchema = WorkflowStateInputSchema.extend({
  transitions: z.array(WorkflowTransitionInputSchema).max(80).optional(),
});
export type AddStateInput = z.infer<typeof AddStateSchema>;

export const UpdateStateSchema = z.object({
  label: z.string().min(1).max(120).optional(),
  ordinal: z.number().int().min(0).optional(),
  category: WorkflowStateCategory.optional(),
  /** Promote / demote the spawn point.  Setting true automatically
   *  flips any other current initial state in the same workflow to
   *  false — the service enforces this in one transaction. */
  isInitial: z.boolean().optional(),
  color: z.string().min(1).max(40).nullable().optional(),
});
export type UpdateStateInput = z.infer<typeof UpdateStateSchema>;

export const AddTransitionSchema = WorkflowTransitionInputSchema;
export type AddTransitionInput = z.infer<typeof AddTransitionSchema>;

/**
 * Chunk 4-E — batch reorder.  Per-PATCH updateState rejects intermediate
 * states because `workflow_ordinals_non_contiguous` runs after every
 * single row write.  This endpoint accepts the full new ordering and
 * runs all UPDATEs + validates the graph once in one transaction.
 *
 * `items` must include every state in the workflow (no partial reorder)
 * and the union of `ordinal` values must be 0..N-1.  Server-side
 * validator enforces both — the FE just sends the post-drag ordering.
 */
export const ReorderStatesSchema = z.object({
  items: z
    .array(
      z.object({
        stateId: z.string().uuid(),
        ordinal: z.number().int().min(0),
      }),
    )
    .min(1),
});
export type ReorderStatesInput = z.infer<typeof ReorderStatesSchema>;

/** Projection returned by getById / list (top-level workflow only). */
export const WorkflowSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  defaultForTeam: z.boolean(),
  enabled: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkflowProjection = z.infer<typeof WorkflowSchema>;

export const WorkflowStateSchema = z.object({
  id: z.string().uuid(),
  workflowId: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  ordinal: z.number().int(),
  category: WorkflowStateCategory,
  isInitial: z.boolean(),
  color: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkflowStateProjection = z.infer<typeof WorkflowStateSchema>;

export const WorkflowTransitionSchema = z.object({
  id: z.string().uuid(),
  workflowId: z.string().uuid(),
  fromStateId: z.string().uuid(),
  toStateId: z.string().uuid(),
  label: z.string(),
  requiredRolePreset: WorkflowRoleRefSchema.nullable(),
  requiresApproval: z.boolean(),
  createdAt: z.string().datetime(),
});
export type WorkflowTransitionProjection = z.infer<typeof WorkflowTransitionSchema>;

/** GET /api/teams/:slug/workflows wrapper. */
export const WorkflowListResponseSchema = z.object({
  items: z.array(WorkflowSchema),
});
export type WorkflowListResponse = z.infer<typeof WorkflowListResponseSchema>;

/** GET /api/workflows/:id/graph returns the full graph at once —
 *  cheaper than 3 round-trips for the editor surface. */
export const WorkflowGraphResponseSchema = z.object({
  workflow: WorkflowSchema,
  states: z.array(WorkflowStateSchema),
  transitions: z.array(WorkflowTransitionSchema),
});
export type WorkflowGraphResponse = z.infer<typeof WorkflowGraphResponseSchema>;

// ── Phase 5 chunk 5 — Workflow Migration Tool ──────────────────────────────

/**
 * POST /api/projects/:projectId/workflow-migration request body.
 *   - `targetWorkflowId` — UUID of the workflow to migrate the project's
 *     tasks onto.  Must belong to the same team.
 *   - `stateMap` — optional explicit `{ oldKey: newKey }` overrides.
 *     Missing keys fall back to auto-match by identical key.
 *   - `dryRun` — true returns the projected mapping without writing.
 */
export const MigrateWorkflowSchema = z.object({
  targetWorkflowId: z.string().uuid(),
  stateMap: z.record(z.string()).optional(),
  dryRun: z.boolean().default(false),
});
export type MigrateWorkflowInput = z.infer<typeof MigrateWorkflowSchema>;

const MigrationEntrySchema = z.object({
  taskId: z.string().uuid(),
  oldStateKey: z.string(),
  newStateKey: z.string(),
});

const MigrationOrphanSchema = z.object({
  taskId: z.string().uuid(),
  oldStateKey: z.string(),
});

export const MigrationResultSchema = z.object({
  projectId: z.string().uuid(),
  targetWorkflowId: z.string().uuid(),
  sourceWorkflowId: z.string().uuid().nullable(),
  totalTasks: z.number().int().nonnegative(),
  migrated: z.array(MigrationEntrySchema),
  orphans: z.array(MigrationOrphanSchema),
  dryRun: z.boolean(),
  appliedAt: z.string().datetime().nullable(),
});
export type MigrationResult = z.infer<typeof MigrationResultSchema>;
